import React, { useState, useMemo, useContext } from "react";
import { useInterviews } from "../../contexts/InterviewsContext";
import { useRecruitmentDashboard } from "../../contexts/RecruitmentDashboardContext";
import Modal from "../../components/UI/Modal";
import InterviewForm from "../../components/Form/InterviewForm";
import ReusableTable from "../../components/UI/ReusableTable";
import Skeleton from "react-loading-skeleton";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import { FaCalendarCheck, FaComments, FaClipboardList, FaUserTie } from "react-icons/fa";
import Drawer from "../../components/UI/Drawer";
import CandidateDetails from "../../components/Details/CandidateDetails";
import { AuthContext } from "../../contexts/AuthContext";

const Interviews = () => {
  const { dashboardData, error } = useRecruitmentDashboard();
  const { roles } = useContext(AuthContext);
  const isHR = roles.includes("HR");
  const { interviews, fetchInterviews, addInterview, updateInterview, deleteInterview, loading, pagination } = useInterviews();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isProceedModalOpen, setIsProceedModalOpen] = useState(false);
  const [proceedCandidate, setProceedCandidate] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // console.log("interviews: ", interviews);

  // Summary cards configuration
  const summaryCards = [
    {
      label: "Interview Scheduled",
      count: dashboardData.interviewScheduledCount || 0,
      icon: <FaCalendarCheck className="text-blue-500 text-2xl" />,
    },
    {
      label: "Interview Feedback Pending",
      count: dashboardData.interviewFeedbackPendingCount || 0,
      icon: <FaComments className="text-blue-500 text-2xl" />,
    },
    {
      label: "Approval Pending",
      count: dashboardData.approvalPendingCount || 0,
      icon: <FaClipboardList className="text-blue-500 text-2xl" />,
    },
    {
      label: "Offer Acceptance Pending",
      count: dashboardData.offerAcceptancePendingCount || 0,
      icon: <FaUserTie className="text-blue-500 text-2xl" />,
    },
  ];


  const openModalForAdd = () => {
    setEditingInterview(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingInterview(null);
  };

  const handleFormSubmit = async (interview) => {
    if (editingInterview) {
      const response = await updateInterview({ ...editingInterview, ...interview });
      // console.log("response: ", response);
      // setNotification("Interview scheduled successfully.");
      setNotification({
        message: "Interview Updated successfully.",
        type: "success",
      });
    } else {
      try {
        const response = await addInterview(interview);
        // console.log("response: ", response);
        // setNotification("Interview scheduled successfully.");
        if (response.success) {
          setNotification({
            message: response.message,
            type: "success",
          });
        } else {
          setNotification({
            message: response.message,
            type: "error",
          });
        }
      } catch {
        // setNotification("Getting issues scheduling interview");
        setNotification({
          message: "Getting issues scheduling interview",
          type: "error",
        });
      }
    }
    closeModal();
  };

  const handleCloseProceedModal = () => {
    setIsProceedModalOpen(false);
    setProceedCandidate(null);
    if (editingInterview) {
      const selectElement = document.getElementById(`action-select-${editingInterview.id}`);
      if (selectElement) {
        selectElement.value = "";
      }
    }
    setEditingInterview(null);
  };

  const openDetailsDrawer = (interview) => {
    setSelectedInterview(interview);
    setIsDrawerOpen(true);
  };

  const closeDetailsDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedInterview(null);
  };

  // Function to update interview status
  const handleStatusUpdate = async (feedback) => {
    if (!selectedInterview) return;
    
    // console.log("Updating status for:", selectedInterview.id, "with:", feedback);

    try {
      await updateInterview({ ...selectedInterview, status: feedback });
      setNotification({
        message: "Interview status updated successfully.",
        type: "success",
      });
      closeDetailsDrawer();
    } catch (error) {
      setNotification({
        message: `Error updating interview status: ${error}`,
        type: "error",
      });
    }
  };


  // new code
  const getColumns = useMemo(() => {
    return (currentPage, pageSize) => {
      const baseColumns = [
        {
          label: "Sr. No",
          field: "sr_no",
          render: (_row, idx) => (currentPage - 1) * pageSize + idx + 1,
        },
        {
          label: "Candidate Name",
          field: "candidate_id",
          render: (row) => (
            <button
              onClick={() => openDetailsDrawer(row)}
              className="text-blue-600 hover:underline hover:underline-offset-4"
            >
              {row.candidate?.name || "Unknown Candidate"}
            </button>
          ),
        },
        {
          label: "Interviewer",
          field: "interviewer",
          render: (row) => row.interviewer ?? row.interview?.interviewer,
        },
        {
          label: "Scheduled At",
          field: "scheduled_at",
          render: (row) =>
            row.scheduled_at
              ? new Date(row.scheduled_at).toLocaleString()
              : row.interview?.scheduled_at
              ? new Date(row.interview.scheduled_at).toLocaleString()
              : "-",
        },
        {
          label: "Candidate Status",
          field: "candidate_status",
          render: (row) => row.candidate?.status || "-",
        },
        {
          label: "Interview Status",
          field: "status",
          render: (row) => row.status ?? row.interview?.status,
        },
      ];

      // Only add Actions column if user is HR
      if (isHR) {
        baseColumns.push({
          label: "Actions",
          field: "actions",
          render: (row) =>
            row.candidate?.status === "Hold" ? (
              <select
                id={`action-select-${row.id}`}
                className="border rounded px-2 py-1"
                onChange={(e) => {
                  if (e.target.value === "proceed") {
                    setProceedCandidate(row.candidate);
                    setEditingInterview(row);
                    setIsProceedModalOpen(true);
                  }
                }}
                defaultValue=""
              >
                <option value="" disabled>
                  Change Status
                </option>
                <option value="proceed">Proceed to interview</option>
              </select>
            ) : null,
        });
      }

      return baseColumns;
    };
  }, [isHR]);
  

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchInterviews(newPage);
    }
  };

  return (
    <div className="flex flex-col gap-y-6 px-1 py-1 font-poppins">
      {/* Summary Cards Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mx-2 my-1">
        {loading
        ? summaryCards.map((_, index) => (
            <div key={index} className="p-4 bg-white dark:bg-gray-900 rounded-lg shadow flex items-center gap-4">
              <Skeleton circle width={48} height={48} />
              <div>
                <Skeleton width={80} />
                <Skeleton width={120} />
              </div>
            </div>
          ))
        : summaryCards.map((card, index) => (
            <div key={index} className="p-4 bg-white dark:bg-gray-900 rounded-lg shadow flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">{card.icon}</div>
              <div>
                <p className="md:text-md text-sm font-semibold dark:text-white">{card.count}</p>
                <p className="text-gray-600 md:text-md text-sm dark:text-white">{card.label}</p>
              </div>
            </div>
          ))}
      </div>
              
      <div className="mx-2 bg-white dark:bg-gray-900 rounded-lg">
        
        {/* notification compo */}
        {notification && (
          <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
        )}

        {/* title */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">Interviews</h2>        
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto">
            Schedule Interview
          </button>
        </div>

        {/* data table */}
        <ReusableTable 
          data={interviews} 
          // columns={columns} 
          columns={getColumns(currentPage, pagination.perPage)}
          loading={loading}
          onFilterChange={(filtering) => setIsFiltering(filtering)}
        />

        {!isFiltering && interviews?.length > 0 && (
          <ReactPaginate
            breakLabel="..."
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            pageCount={pagination.totalPages}
            renderOnZeroPageCount={null}
            nextLabel="Next >"
            previousLabel="< Prev"
            previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
            nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
            containerClassName="flex items-center justify-center mt-4"
            pageClassName="mx-1 px-3 py-1 border rounded"
            activeClassName="bg-blue-500 text-white"
          />
        )}

        {/* schedule interview modal */}
        <Modal title={"Schedule Interview"} isOpen={isModalOpen} onRequestClose={closeModal}>
          <InterviewForm onSubmit={handleFormSubmit} closeModal={closeModal}  />
        </Modal>

        <Modal
          title="Proceed to Interview"
          isOpen={isProceedModalOpen}
          onRequestClose={handleCloseProceedModal}
        >
          {proceedCandidate && (
            <InterviewForm
              initialValues={{
                candidate_id: proceedCandidate.id,
                interviewer: "",
                scheduled_at: "",
                status: "Scheduled",
                interview_type: "",
                interview_link: "",
                location: "",
              }}
              onSubmit={async (formData) => {
                await updateInterview({ ...editingInterview, ...formData });
                handleCloseProceedModal();
              }}
              closeModal={handleCloseProceedModal}
            />
          )}
        </Modal>

        {/* Drawer */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={closeDetailsDrawer}
          customWidth="w-3/5"
          ChilDiv=""
          ChildParent=""
          showClose={false}
          border_radius="md:rounded-none"
        >
          {selectedInterview ? (
            <CandidateDetails 
              candidate={selectedInterview.candidate} 
              interview={selectedInterview} 
              onUpdateStatus={handleStatusUpdate} 
              onClose={closeDetailsDrawer}
            />
          ) : (
            <p className="text-center text-gray-500"> </p>
          )}
        </Drawer>
      </div>
    </div>
  );
};

export default React.memo(Interviews);
